import { useEffect, useState } from "react";
import { listAllLocations, type StudioLocationsRequestOptions } from "@v0y4ge/api-client";
import { toUILocation, type UILocation } from "./locations-adapter";
import { can } from "../../shell/access";

/**
 * useLocations — the Locations list data source. Calls the typed api-client
 * `listLocations` choke point (→ MSW mock store in this env / real infra on
 * deploy), validates against LocationListResponse, and maps each row via the
 * adapter. No static seed data.
 *
 * The API base URL is injected by the host (studio-shell provides the dynamic
 * settings base); in the design/preview env the MSW worker intercepts regardless
 * of the value, so a placeholder is fine here and the real base is wired on deploy.
 */
const REQUEST_OPTS: StudioLocationsRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};
/** Shared request options for ad-hoc locations ops outside the hook (UIR-35). */
export const LOCATIONS_REQUEST_OPTS = REQUEST_OPTS;

export interface LocationsState {
  locations: UILocation[];
  total: number;
  loading: boolean;
  error: string | null;
}

/**
 * ONE READ, SHARED (CD, 2026-09-17).
 *
 * Every mounted consumer of this hook used to issue its own
 * `GET /v1/admin/settings/locations?limit=200`. Several screens read locations
 * at once (the queue designer's Setup, the picker's option source, the
 * locations screens), so a single navigation fired the same request four times
 * within a second or two — the first answered 200 with all twenty rows and the
 * rest came back **429**, and the Add Queue control rendered the rate-limited
 * failure as "No locations yet". The fix that matters is not a nicer error: it
 * is not asking four times.
 *
 * So the promise is module-scoped and shared. Concurrent mounts await the same
 * request; later mounts reuse the settled result rather than re-reading;
 * `reload()` clears the cache and starts exactly one new read that every live
 * consumer receives. Nothing is cached across a tenant switch, because the
 * shell remounts the tree and `reload` is what the locations screens call
 * after a write.
 */
type LocationsPayload = { locations: UILocation[]; total: number };
let sharedRead: Promise<LocationsPayload> | null = null;
let sharedResult: LocationsPayload | null = null;
const subscribers = new Set<() => void>();

function readLocations(): Promise<LocationsPayload> {
  if (sharedRead) return sharedRead;
  sharedRead = listAllLocations({ limit: 200 }, REQUEST_OPTS)
    .then((res: any) => {
      const rows = (res.items ?? res.data ?? res.locations ?? []).map(toUILocation);
      const total = res.locationsPagination?.total ?? res.pagination?.total ?? rows.length;
      sharedResult = { locations: rows, total };
      return sharedResult;
    })
    .catch((e: unknown) => {
      // The next mount (or an explicit reload) may legitimately retry — a 429
      // is transient by definition — so the FAILED promise is not kept.
      sharedRead = null;
      throw e;
    });
  return sharedRead;
}

/** Drop the shared read so the next consumer fetches again. */
export function invalidateLocations(): void {
  sharedRead = null;
  sharedResult = null;
  subscribers.forEach((fn) => fn());
}

export function useLocations(): LocationsState & { reload: () => void } {
  const [state, setState] = useState<LocationsState>(() => (sharedResult
    ? { locations: sharedResult.locations, total: sharedResult.total, loading: false, error: null }
    : { locations: [], total: 0, loading: true, error: null }));
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Re-render this consumer when someone else invalidates the shared read.
    const wake = () => { if (!cancelled) setNonce((n) => n + 1); };
    subscribers.add(wake);
    if (sharedResult) {
      setState({ locations: sharedResult.locations, total: sharedResult.total, loading: false, error: null });
      return () => { cancelled = true; subscribers.delete(wake); };
    }
    /* CAPABILITY GATE (CD docket gm39-followups, 2026-09-18 item 1). The
       settings/locations route is `locations.read`; a caller without it (Team
       Member's My Day LocationFilter) used to fire the shared read anyway and eat
       a 403. Same shape as useStaff.ts's `if (can("team.read")) hydrate()`:
       skip the fetch, settle as an empty, not-loading list. */
    if (!can("locations.read")) {
      setState({ locations: [], total: 0, loading: false, error: null });
      return () => { cancelled = true; subscribers.delete(wake); };
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    /* No AbortController: the request is SHARED now, so one consumer
       unmounting must not cancel a read the others are waiting on. Staleness
       is handled by `cancelled` per consumer instead. */
    readLocations()
      .then((payload) => {
        if (cancelled) return;
        setState({ locations: payload.locations, total: payload.total, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ locations: [], total: 0, loading: false, error: e instanceof Error ? e.message : "Failed to load locations." });
      });
    return () => { cancelled = true; subscribers.delete(wake); };
  }, [nonce]);

  return { ...state, reload: () => { invalidateLocations(); setNonce((n) => n + 1); } };
}
